import type { IBranchFull } from "@/api/admin/branches/types";
import {
  POS_QR_PATTERNS,
  type PosIntegrationResponse,
  type PosIntegrationType,
  PosIntegrator,
  PosName,
  type PosQrPattern,
  type UpsertPosIntegrationRequest,
} from "@/api/admin/posIntegration/types";

import { type BranchPos, POS_WITHOUT_RAW_TYPE } from "../types";

function branchPosNameToForm(
  raw: PosName | undefined | null
): Pick<BranchPos, "posName" | "posRawType"> {
  if (!raw) {
    return { posName: undefined, posRawType: undefined };
  }
  if (POS_WITHOUT_RAW_TYPE.includes(raw)) {
    return { posName: raw, posRawType: undefined };
  }
  return { posName: PosName.UNKNOWN, posRawType: raw };
}

function isSupportedPosType(type: string | null | undefined): type is PosName {
  return !!type && POS_WITHOUT_RAW_TYPE.includes(type as PosName);
}

export function normalizePosIntegrator(
  raw: string | null | undefined
): PosIntegrator {
  if (!raw) return PosIntegrator.REACHWARE;
  const upper = raw.toUpperCase();
  if (upper === "INHOUSE") return PosIntegrator.INHOUSE;
  if (upper === "REACHWARE") return PosIntegrator.REACHWARE;
  return (raw as PosIntegrator) || PosIntegrator.REACHWARE;
}

type BranchPosSource = Pick<
  IBranchFull,
  | "isPosIntegrated"
  | "posClientId"
  | "posBranchId"
  | "tableNumberLength"
  | "posName"
>;

/** Prefer dedicated POS API response; fall back to branch fields when GET is 404. */
export function posIntegrationToFormSlice(
  pos: PosIntegrationResponse | null,
  branch: BranchPosSource | undefined
): Pick<
  BranchPos,
  | "isPosIntegrated"
  | "posName"
  | "posRawType"
  | "posClientId"
  | "posBranchId"
  | "posTenderId"
  | "posDiscountId"
  | "baseUrl"
  | "integrator"
  | "qrScannerEnabled"
  | "qrPattern"
  | "tableNumberLength"
  | "isSettlementEnabled"
  | "autoClose"
  | "isBillLookupEnabled"
  | "isTableNumberLookupEnabled"
  | "isTableIdLookupEnabled"
  | "tableIdMappingFileName"
  | "tableIdMappingRemoved"
  | "tableIdMappingFile"
> {
  if (pos !== null) {
    const supported = isSupportedPosType(pos.type) ? pos.type : null;

    const typePick =
      supported !== null
        ? { posName: supported, posRawType: undefined }
        : pos.type
          ? {
              posName: PosName.UNKNOWN,
              posRawType: pos.type.trim() || undefined,
            }
          : { posName: undefined, posRawType: undefined };

    const qrPattern = pos.qrPattern ?? "";

    return {
      isPosIntegrated: pos.isEnabled,
      ...typePick,
      posClientId: pos.clientId ?? undefined,
      posBranchId: pos.branchIdOnPos ?? undefined,
      posTenderId: pos.tenderId ?? undefined,
      posDiscountId: pos.discountId ?? undefined,
      baseUrl: pos.baseUrl ?? undefined,
      integrator: normalizePosIntegrator(pos.integrator),
      qrScannerEnabled: Boolean(pos.qrPattern),
      qrPattern,
      tableNumberLength:
        pos.tableNumberLength != null ? String(pos.tableNumberLength) : "3",
      isSettlementEnabled: Boolean(pos.isSettlementEnabled),
      autoClose: Boolean(pos.autoClose),
      isBillLookupEnabled: Boolean(pos.isBillLookupEnabled),
      isTableNumberLookupEnabled: Boolean(pos.isTableNumberLookupEnabled),
      isTableIdLookupEnabled: Boolean(pos.isTableIdLookupEnabled),
      tableIdMappingFileName:
        pos.tableIdMapping?.fileName ??
        (pos.tableIdMapping?.rowCount ? "table-id-mapping.csv" : null),
      tableIdMappingRemoved: false,
      tableIdMappingFile: null,
    };
  }

  const { posName, posRawType } = branchPosNameToForm(
    branch?.posName as PosName
  );

  return {
    isPosIntegrated: Boolean(branch?.isPosIntegrated),
    posName,
    posRawType,
    posClientId: branch?.posClientId ?? undefined,
    posBranchId: branch?.posBranchId ?? undefined,
    baseUrl: undefined,
    integrator: PosIntegrator.REACHWARE,
    qrScannerEnabled: false,
    qrPattern: "",
    tableNumberLength:
      branch?.tableNumberLength != null
        ? String(branch.tableNumberLength)
        : "3",
    isSettlementEnabled: false,
    autoClose: false,
    isBillLookupEnabled: false,
    isTableNumberLookupEnabled: false,
    isTableIdLookupEnabled: false,
    tableIdMappingFileName: null,
    tableIdMappingRemoved: false,
    tableIdMappingFile: null,
  };
}

export function buildUpsertPosIntegrationRequest(
  branchId: string,
  data: BranchPos,
  updatedAt?: string
): UpsertPosIntegrationRequest {
  const tableNum = Math.min(
    10,
    Math.max(1, Number(data.tableNumberLength) || 3)
  );

  const trimmedBaseUrl = data.baseUrl?.trim();

  const credentials = {
    clientId: data.posClientId ?? "",
    branchIdOnPos: data.posBranchId ?? "",
    tenderId: data.posTenderId ?? "",
    discountId: data.posDiscountId ?? "",
    autoClose: data.autoClose ?? false,
    ...(trimmedBaseUrl ? { baseUrl: trimmedBaseUrl } : {}),
  };

  const posType =
    data.posName === PosName.UNKNOWN ? data.posRawType : data.posName;

  // Pattern choice is hidden in admin; any non-null value enables the scanner.
  let qrPattern: PosQrPattern | null = null;
  if (data.qrScannerEnabled) {
    const pattern = data.qrPattern;
    qrPattern =
      pattern && POS_QR_PATTERNS.includes(pattern as PosQrPattern)
        ? (pattern as PosQrPattern)
        : PosQrPattern.QR_CENTER;
  }

  const marker = updatedAt === undefined ? {} : { updatedAt };

  if (!data.isPosIntegrated) {
    return {
      branchId,
      isEnabled: false,
      tableNumberLength: tableNum,
      isSettlementEnabled: data.isSettlementEnabled ?? false,
      isBillLookupEnabled: data.isBillLookupEnabled ?? false,
      isTableNumberLookupEnabled: data.isTableNumberLookupEnabled ?? false,
      isTableIdLookupEnabled: data.isTableIdLookupEnabled ?? false,
      ...(posType ? { type: posType as PosIntegrationType } : {}),
      ...(data.integrator ? { integrator: data.integrator } : {}),
      qrPattern,
      credentials,
      ...marker,
    };
  }

  if (
    !posType ||
    (data.posName !== PosName.UNKNOWN &&
      !POS_WITHOUT_RAW_TYPE.includes(posType as PosName))
  ) {
    throw new Error(`Unsupported POS type: ${String(posType)}`);
  }

  const integrator = data.integrator;
  if (
    integrator !== PosIntegrator.REACHWARE &&
    integrator !== PosIntegrator.INHOUSE
  ) {
    throw new Error(`Invalid integrator: ${String(integrator)}`);
  }

  return {
    branchId,
    isEnabled: true,
    type: posType as PosIntegrationType,
    integrator,
    qrPattern,
    tableNumberLength: tableNum,
    isSettlementEnabled: data.isSettlementEnabled ?? false,
    isBillLookupEnabled: data.isBillLookupEnabled ?? false,
    isTableNumberLookupEnabled: data.isTableNumberLookupEnabled ?? false,
    isTableIdLookupEnabled: data.isTableIdLookupEnabled ?? false,
    credentials,
    ...marker,
  };
}

/** POST bind when no row exists yet (GET 404); otherwise PATCH. */
export function shouldPostPosBind(
  existing: PosIntegrationResponse | null
): boolean {
  return existing === null;
}
